// 用户认证相关的 API 请求统一管理
import { toast } from 'sonner';
import { getDeviceId } from '@/lib/device';
import { normalizePhoneNumber } from '@/lib/phone';
import { logSentryError, logSentryInfo, logSentryWarn } from '@/lib/sentryLogs';
import { AuthSessionError } from '@/lib/authSession';

const API_BASE_URL = '/api';

const INVITE_CODE_ERROR_CODES = new Set([60005, 60006, 60007, 60008, 60009]);

export class InviteCodeError extends Error {
  constructor(public readonly code: number, message: string) {
    super(message);
    this.name = 'InviteCodeError';
  }
}

export interface UserInfo {
  user_id: string;
  username: string;
  email: string;
  phone?: string;
  nickname?: string;
  avatar?: string;
  roles: string[];
  created_at: string;
  team_id?: string;
}

export interface AuthRequest {
  invitation_code?: string;
  username?: string;
  email?: string;
  password?: string;
  nickname?: string;
  avatar?: string;
  token?: string;
  credential?: string;
  identifier?: string;
  device_id: string;
  partner?: string;
}

export interface AuthResponse {
  token: string;
  user_id: string;
  expires_at: string;
  roles?: string[];
  is_new_user?: boolean;
  error_code?: string | number;
  message?: string;
}

export type GetUserInfoResponse = UserInfo;

export interface SessionStoragePayload {
  token?: string;
  user_id?: string;
  expires_at?: string;
}

// localStorage 存储键名
 const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  USER_ID: 'user_id',
  EXPIRES_AT: 'expires_at'
 };

// Guard for environments without localStorage (SSR)
const HAS_LOCAL_STORAGE = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  const responseText = await response.text();

  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return null;
  }
}

function getResponseCode(payload: unknown): string | number | undefined {
  if (!isObjectRecord(payload)) {
    return undefined;
  }

  const responseCode = payload.code;
  return typeof responseCode === 'string' || typeof responseCode === 'number'
    ? responseCode
    : undefined;
}

function getResponseMessage(payload: unknown, fallbackMessage: string): string {
  if (!isObjectRecord(payload)) {
    return fallbackMessage;
  }

  return typeof payload.message === 'string' && payload.message.trim()
    ? payload.message
    : fallbackMessage;
}

// 统一登录/注册 - 使用邀请码和 Google 用户信息
export async function authenticateWithInvitation(
  invitationCode?: string | null,
  googleUserInfo?: {
    email: string;
    name: string;
    avatar: string;
    id: string;
  },
  credential?: string,
  partner?: string
): Promise<AuthResponse> {
  const authData: AuthRequest = {
    device_id: getDeviceId(),
  };

  // 如果有邀请码，传给后端
  if (invitationCode) {
    authData.invitation_code = invitationCode;
  }

  // 如果有合作方参数，传给后端
  if (partner) {
    authData.partner = partner;
  }

  // 如果有 Google 用户信息，添加到请求中
  if (googleUserInfo) {
    authData.email = googleUserInfo.email;
    authData.username = googleUserInfo.email.split('@')[0];
    authData.nickname = googleUserInfo.name;
    authData.avatar = googleUserInfo.avatar;
    authData.token = googleUserInfo.id;
    authData.credential = credential;
    authData.identifier = 'google';
  }

  try {
    logSentryInfo('login.auth.request_started', {
      log_source: 'login_flow',
      auth_step: 'request_started',
      auth_identifier: authData.identifier ?? 'unknown',
      invite_code: invitationCode ?? '',
      invite_code_length: invitationCode?.length ?? 0,
      has_invite_code: Boolean(invitationCode),
      google_user_id: googleUserInfo?.id ?? '',
      google_user_email: googleUserInfo?.email ?? '',
      auth_request_username: authData.username ?? '',
      auth_request_nickname: authData.nickname ?? '',
      device_id: authData.device_id,
      has_credential: Boolean(credential),
    });

    const response = await fetch(`${API_BASE_URL}/users/auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(authData),
    });

    const data = await response.json();

    logSentryInfo('login.auth.response_received', {
      log_source: 'login_flow',
      auth_step: 'response_received',
      invite_code: invitationCode ?? '',
      has_invite_code: Boolean(invitationCode),
      google_user_email: googleUserInfo?.email ?? '',
      response_ok: response.ok,
      http_status: response.status,
      response_code: typeof data?.code === 'number' || typeof data?.code === 'string' ? data.code : '',
      response_message: typeof data?.message === 'string' ? data.message : '',
      auth_user_id: typeof data?.data?.user_id === 'string'
        ? data.data.user_id
        : typeof data?.user_id === 'string'
          ? data.user_id
          : '',
      auth_has_token: Boolean(data?.data?.token ?? data?.token),
      is_new_user: Boolean(data?.data?.is_new_user ?? data?.is_new_user),
    });

    if (INVITE_CODE_ERROR_CODES.has(data.code)) {
      logSentryWarn('login.auth.invite_code_error', {
        log_source: 'login_flow',
        auth_step: 'invite_code_error',
        invite_code: invitationCode ?? '',
        invite_code_length: invitationCode?.length ?? 0,
        google_user_id: googleUserInfo?.id ?? '',
        google_user_email: googleUserInfo?.email ?? '',
        response_code: data.code,
        response_message: data.message || '',
      });
      throw new InviteCodeError(data.code, data.message || '');
    }

    if (!response.ok) {
      logSentryWarn('login.auth.http_error', {
        log_source: 'login_flow',
        auth_step: 'http_error',
        invite_code: invitationCode ?? '',
        google_user_id: googleUserInfo?.id ?? '',
        google_user_email: googleUserInfo?.email ?? '',
        http_status: response.status,
        response_code: typeof data?.code === 'number' || typeof data?.code === 'string' ? data.code : '',
        response_message: data.message || 'Authentication failed',
      });
      toast.error(data.message || 'Authentication failed');
    }

    const authResponse = data.data ?? data;

    logSentryInfo('login.auth.response_resolved', {
      log_source: 'login_flow',
      auth_step: 'response_resolved',
      invite_code: invitationCode ?? '',
      google_user_email: googleUserInfo?.email ?? '',
      auth_user_id: authResponse.user_id ?? '',
      auth_has_token: Boolean(authResponse.token),
      auth_error_code: authResponse.error_code ?? '',
      auth_message: authResponse.message ?? '',
      is_new_user: Boolean(authResponse.is_new_user),
    });

    return authResponse;
  } catch (error) {
    if (error instanceof InviteCodeError) {
      throw error;
    }
    const errMsg = getErrorMessage(error);
    logSentryError('login.auth.request_failed', {
      log_source: 'login_flow',
      auth_step: 'request_failed',
      invite_code: invitationCode ?? '',
      invite_code_length: invitationCode?.length ?? 0,
      has_invite_code: Boolean(invitationCode),
      google_user_id: googleUserInfo?.id ?? '',
      google_user_email: googleUserInfo?.email ?? '',
      error_message: errMsg,
    });
    toast.error(`Authentication error${errMsg ? ': ' + errMsg : ''}`);
    throw error;
  }
}

// 获取用户信息
export async function getUserInfo(userId?: string): Promise<GetUserInfoResponse | null> {
  try {
    const token = HAS_LOCAL_STORAGE ? (localStorage.getItem(STORAGE_KEYS.TOKEN) as string | null) : null;
    const storeUserId = HAS_LOCAL_STORAGE ? (localStorage.getItem(STORAGE_KEYS.USER_ID) as string | null) : null;

    if (!userId && !storeUserId) {
      return null;
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${userId || storeUserId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });

    const data = await parseJsonSafely(response);

    if (!response.ok) {
      throw new AuthSessionError(
        getResponseMessage(data, 'Failed to get user info'),
        {
          status: response.status,
          code: getResponseCode(data),
          payload: data,
        }
      );
    }

    if (isObjectRecord(data) && 'data' in data) {
      return (data.data as GetUserInfoResponse | null) ?? null;
    }

    return (data as GetUserInfoResponse | null) ?? null;
  } catch (error) {
    console.error('Get user info error:', error);
    throw error;
  }
}

// 更新用户信息
export async function updateUserInfo(
  userId: string,
  updateData: {
    nickname?: string;
    avatar?: string;
  }
): Promise<{ user_id: string; message: string }> {
  try {
    const token = HAS_LOCAL_STORAGE ? (localStorage.getItem(STORAGE_KEYS.TOKEN) as string | null) : null;
    
    const response = await fetch(`${API_BASE_URL}/users/${userId}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(updateData),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update user info');
    }

    return data;
  } catch (error) {
    console.error('Update user info error:', error);
    throw error;
  }
}

// 邮箱验证码发送响应
export interface SendVerificationCodeResponse {
  email: string;
  expires_in: number;
  sent_at: string;
}

export interface PasswordResetResponse {
  message: string;
}

export interface SendSmsCodeResponse {
  message: string;
}

// 邮箱注册/登录用户信息
export interface EmailUser {
  id: string;
  email: string;
  phone?: string;
  username: string;
  created_at?: string;
  last_login_at?: string;
}

// 邮箱认证令牌
export interface EmailToken {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

// 邮箱注册/登录响应
export interface EmailAuthResponse {
  user?: EmailUser;
  token: string | EmailToken;
  user_id?: string;
  expires_at?: string;
  refresh_token?: string;
  refresh_token_expires_at?: string;
  username?: string;
  email?: string;
  phone?: string;
  roles?: string[];
  session_id?: string;
  device_id?: string;
  teams?: unknown[];
  is_new_user?: boolean;
}

export function resolveAuthStoragePayload(
  res: Pick<EmailAuthResponse, 'token' | 'expires_at' | 'user_id' | 'user'>
): SessionStoragePayload {
  const token = typeof res.token === 'string' ? res.token : res.token?.access_token;
  const expiresAt = res.expires_at
    ?? (typeof res.token === 'object' && typeof res.token?.expires_in === 'number'
      ? new Date(Date.now() + res.token.expires_in * 1000).toISOString()
      : undefined);
  const userId = res.user_id ?? res.user?.id;

  return { token, user_id: userId, expires_at: expiresAt };
}

export function hasCompleteAuthStoragePayload(
  authData: SessionStoragePayload
): authData is { token: string; user_id: string; expires_at: string } {
  return Boolean(authData.token && authData.user_id && authData.expires_at);
}

// 发送邮箱验证码
export async function sendVerificationCode(email: string): Promise<SendVerificationCodeResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/users/send-verification-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to send verification code');
    }

    return data.data;
  } catch (error) {
    console.error('Send verification code error:', error);
    throw error;
  }
}

export async function sendSmsCode(phone: string): Promise<SendSmsCodeResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/users/send-sms-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phone: normalizePhoneNumber(phone) }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to send verification code');
    }

    return data.data;
  } catch (error) {
    console.error('Send SMS code error:', error);
    throw error;
  }
}

// 发送密码重置验证码
export async function sendPasswordResetCode(email: string): Promise<PasswordResetResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/users/password/reset-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to send password reset code');
    }

    return data.data;
  } catch (error) {
    console.error('Send password reset code error:', error);
    throw error;
  }
}

// 邮箱注册
export async function emailRegister(
  email: string,
  verificationCode: string,
  password: string,
  invitationCode?: string,
  partner?: string
): Promise<EmailAuthResponse> {
  try {
    const body: Record<string, string> = {
      email,
      verification_code: verificationCode,
      password,
    };
    if (invitationCode) {
      body.invitation_code = invitationCode;
    }
    if (partner) {
      body.partner = partner;
    }

    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Registration failed');
    }

    return data.data;
  } catch (error) {
    console.error('Email register error:', error);
    throw error;
  }
}

// 邮箱登录
export async function emailLogin(
  email: string,
  password: string,
  invitationCode?: string,
  partner?: string
): Promise<EmailAuthResponse> {
  try {
    const body: Record<string, string> = {
      email,
      password,
    };

    if (invitationCode) {
      body.invitation_code = invitationCode;
    }

    if (partner) {
      body.partner = partner;
    }

    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    return data.data;
  } catch (error) {
    console.error('Email login error:', error);
    throw error;
  }
}

export async function phoneLogin(params: {
  phone: string;
  code: string;
  deviceId?: string;
  invitationCode?: string;
  partner?: string;
}): Promise<EmailAuthResponse> {
  try {
    const body: Record<string, string> = {
      phone: normalizePhoneNumber(params.phone),
      code: params.code.trim(),
      device_id: params.deviceId ?? getDeviceId(),
    };

    if (params.invitationCode) {
      body.invitation_code = params.invitationCode;
    }

    if (params.partner) {
      body.partner = params.partner;
    }

    const response = await fetch(`${API_BASE_URL}/users/phone-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    return data.data;
  } catch (error) {
    console.error('Phone login error:', error);
    throw error;
  }
}

// 重置密码
export async function resetPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<PasswordResetResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/users/password/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        code,
        new_password: newPassword,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset password');
    }

    return data.data;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
}

// 修改密码
export async function changePassword(
  userId: string,
  oldPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> {
  try {
    const token = HAS_LOCAL_STORAGE ? (localStorage.getItem(STORAGE_KEYS.TOKEN) as string | null) : null;
    
    const response = await fetch(`${API_BASE_URL}/users/${userId}/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to change password');
    }

    return data;
  } catch (error) {
    console.error('Change password error:', error);
    throw error;
  }
}

// localStorage 操作函数
export const storage = {
  // 存储认证信息
  setAuthData: (authData: AuthResponse) => {
    if (!HAS_LOCAL_STORAGE) return;
    if (authData.token) localStorage.setItem(STORAGE_KEYS.TOKEN, authData.token);
    if (authData.user_id) localStorage.setItem(STORAGE_KEYS.USER_ID, authData.user_id);
    if (authData.expires_at) localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, authData.expires_at);
  },

  // 获取认证信息
  getAuthData: () => {
    if (!HAS_LOCAL_STORAGE) {
      return { token: null as string | null, userId: null as string | null, expiresAt: null as string | null };
    }
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);

    return {
      token,
      userId,
      expiresAt,
    };
  },

  // 检查是否已登录（包含过期检查）
  isLoggedIn: () => {
    const { token, userId } = storage.getAuthData();
    
    if (!token || !userId) {
      return false;
    }

    // 检查 token 是否过期
    // 前端不校验有效期，时间不准
    // if (expiresAt) {
    //   const expiryTime = new Date(expiresAt).getTime();
    //   const currentTime = new Date().getTime();
    //   if (currentTime >= expiryTime) {
    //     storage.clearAuthData();
    //     return false;
    //   }
    // }

    return true;
  },

  // 清除认证信息
  clearAuthData: () => {
    if (!HAS_LOCAL_STORAGE) return;
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
  },

  // 获取 Authorization Header
  getAuthHeader: () => {
    if (!HAS_LOCAL_STORAGE) return null;
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? `Bearer ${token}` : null;
  },
};
